import { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import { format, parseISO } from "date-fns";
import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import {
  fetchClientOverview,
  clearStatus,
} from "../../../redux/slices/clientOverviewSlice";
import Table from "../../../components/Table";
import { exportClientOverviewToXLSX } from "../../../utils/xlsxExport";
import { PageHeader } from "../../../ui";

function formatLastLogin(value: any) {
  if (!value) return "Never";
  try {
    return format(parseISO(value), "MM/dd/yyyy HH:mm");
  } catch {
    return "Never";
  }
}

function ProjectsCell({ customers }: any) {
  if (!customers || customers.length === 0)
    return <Typography variant="body2">—</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {customers.map((c: any) => (
        <Typography key={c.customerId} variant="body2">
          {c.projectName}
        </Typography>
      ))}
    </Box>
  );
}

function PaymentMethodsCell({ customers }: any) {
  if (!customers || customers.length === 0)
    return <Typography variant="body2">—</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {customers.map((c: any) => (
        <Box
          key={c.customerId}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Typography variant="body2">
            {c.favoritePaymentMethod || "No payments yet"}
          </Typography>
          {c.favoritePaymentMethod && (
            <Chip
              label={c.onlinePayment ? "Online" : "Offline"}
              color={c.onlinePayment ? "success" : "default"}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

const ClientOverview = () => {
  const dispatch = useAppDispatch();
  const { rows, status, error } = useAppSelector((state) => state.clientOverview);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchClientOverview());
    }
  }, [status, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearStatus());
    };
  }, [dispatch]);

  const handleExport = () => {
    exportClientOverviewToXLSX(rows);
  };

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="Client Overview"
        summary="Who can sign in for each client, how they pay, and when they last visited."
        actions={
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={status !== "succeeded" || rows.length === 0}
          >
            Export to Excel
          </Button>
        }
      />
      <Box>
        {status === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {status === "failed" && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Failed to load client overview. Please try again."}
          </Alert>
        )}
        {status !== "loading" && status !== "failed" && (
          <Table
            enableColumnSearch
            rowKey="userId"
            defaultSortKey="clientName"
            defaultSortOrder="asc"
            headers={[
              { key: "clientName", displayName: "Client Name" },
              { key: "email", displayName: "Client User Login" },
              {
                key: "customers",
                displayName: "Project(s)",
                format: ({ value }: any) => <ProjectsCell customers={value} />,
                searchValue: ({ value }: any) =>
                  Array.isArray(value)
                    ? value
                        .map((customer: any) => customer?.projectName || "")
                        .filter(Boolean)
                        .join(" ")
                    : "",
              },
              {
                key: "activatedInPortal",
                displayName: "Activated in Portal?",
                disableColumnSearch: true,
                format: ({ value }: any) => (
                  <Chip
                    label={value ? "Active" : "Pending"}
                    color={value ? "success" : "warning"}
                    size="small"
                  />
                ),
              },
              {
                key: "customers",
                displayName: "Preferred Payment Method",
                disableColumnSearch: true,
                format: ({ value }: any) => <PaymentMethodsCell customers={value} />,
              },
              {
                key: "lastLogin",
                displayName: "Last Login",
                disableColumnSearch: true,
                sortValue: (value: any) =>
                  value ? new Date(value).getTime() : null,
                format: ({ value }: any) => formatLastLogin(value),
              },
            ]}
            rows={rows}
            loading={status === "loading"}
          />
        )}
      </Box>
    </>
  );
};

export default ClientOverview;
